import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk, RootState } from '.';
import { PointOfSale } from '../interface';
import {
  getActiveAnyUserPointOfSaleId,
  getAnyUserPointOfSaleId,
} from '../utils/multi-store/any-user';
import {
  resolveDraftApply,
  sanitizeAddedPointOfSaleIds,
} from '../utils/multi-store/selection';
import { serHourSlot, setDateSlot } from './appointment-slot';
import { setPointOfSaleFromCustomPointOfSaleId } from './point-of-sale';
import { resetUser } from './user';

// Smart multi-store (solo aziende FOCUS CLOUD): centri ottici aggiunti al calendario dello
// step "quando". originPointOfSale è il centro scelto nello step "dove" e resta lo stesso
// anche dopo i cambi centro fatti dal calendario unificato.
export interface MultiStoreSliceInterface {
  originPointOfSale?: PointOfSale;
  serviceId?: string;
  addedPointOfSaleIds: string[];
  isPanelOpen: boolean;
  // centro raggiunto azzerando l'operatore scelto, che lì non lavora: la sua scheda «Dove
  // vuoi andare» dice «Con il primo operatore disponibile». Sta qui e non nel calendario
  // perché lo imposta anche il ritorno all'origin dal pannello.
  anyUserPointOfSaleId?: string;
}

// Con l'origin fanno 5 centri: il massimo che il backend accetta in una richiesta
export const MULTI_STORE_MAX_ADDED = 4;

const initialState: MultiStoreSliceInterface = {
  addedPointOfSaleIds: [],
  isPanelOpen: false,
};

// La sessione vale per il servizio con cui è partita e finché il centro corrente è l'origin
// o un aggiunto: se il cliente cambia servizio o centro dagli step precedenti si torna al
// calendario classico senza toccare lo step "dove"
const isSessionValid = (
  multiStore: MultiStoreSliceInterface,
  serviceId: string,
  currentPointOfSaleId: string
): boolean =>
  multiStore.originPointOfSale !== undefined &&
  multiStore.serviceId === serviceId &&
  (multiStore.originPointOfSale.id === currentPointOfSaleId ||
    multiStore.addedPointOfSaleIds.includes(currentPointOfSaleId));

export const MultiStoreSlice = createSlice({
  name: 'MultiStore',
  initialState,
  reducers: {
    // Al primo utilizzo fissa origin = centro corrente e servizio; una sessione valida resta com'è
    startMultiStore: (
      state,
      action: PayloadAction<{ pointOfSale: PointOfSale; serviceId: string }>
    ) => {
      const { pointOfSale, serviceId } = action.payload;
      if (isSessionValid(state, serviceId, pointOfSale.id)) return;
      state.originPointOfSale = pointOfSale;
      state.serviceId = serviceId;
      state.addedPointOfSaleIds = [];
      state.isPanelOpen = false;
      state.anyUserPointOfSaleId = undefined;
    },
    // Aggiunti confermati dal pannello, impostati in blocco: senza l'origin, senza doppioni e
    // al massimo MULTI_STORE_MAX_ADDED. Il pannello si chiude; senza aggiunti si torna al
    // calendario classico.
    setMultiStorePointOfSales: (state, action: PayloadAction<string[]>) => {
      if (state.originPointOfSale === undefined) return;
      state.addedPointOfSaleIds = sanitizeAddedPointOfSaleIds(
        action.payload,
        state.originPointOfSale.id,
        MULTI_STORE_MAX_ADDED
      );
      state.isPanelOpen = false;
    },
    openMultiStorePanel: (state) => {
      state.isPanelOpen = true;
    },
    closeMultiStorePanel: (state) => {
      state.isPanelOpen = false;
    },
    setMultiStoreAnyUserPointOfSale: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.anyUserPointOfSaleId = action.payload;
    },
    resetMultiStore: () => initialState,
  },
});

export const {
  startMultiStore,
  setMultiStorePointOfSales,
  openMultiStorePanel,
  closeMultiStorePanel,
  setMultiStoreAnyUserPointOfSale,
  resetMultiStore,
} = MultiStoreSlice.actions;

// Cambio centro dal calendario unificato mantenendo giorno e ora: non si usa
// ListPointOfSales.handleSelected, che azzera lo slot. L'ordine conta: setDateSlot azzera l'ora.
export const switchMultiStorePointOfSale =
  (
    pointOfSale: PointOfSale,
    isAnyUser: boolean,
    date: string,
    hour: string
  ): AppThunk =>
  (dispatch, getState) => {
    const hadUser = getState().user.activeUser !== undefined;
    // azzera anche studioId e customPointOfSaleId/Name: tornando su "dove" non si
    // riseleziona il centro del link
    dispatch(setPointOfSaleFromCustomPointOfSaleId(pointOfSale));
    // l'operatore scelto non lavora nel centro: si passa a "qualsiasi operatore" e la
    // scheda del centro lo ricorda
    if (isAnyUser) dispatch(resetUser());
    dispatch(
      setMultiStoreAnyUserPointOfSale(
        getAnyUserPointOfSaleId(pointOfSale.id, isAnyUser, hadUser)
      )
    );
    dispatch(setDateSlot(date));
    dispatch(serHourSlot(hour));
  };

// Conferma della bozza del pannello. Se il centro corrente non è più incluso l'appuntamento
// torna all'origin con lo stesso giorno e la stessa ora, prima di cambiare gli aggiunti così
// la sessione resta valida; se lì l'ora non è libera la azzera il calendario (unificato o
// HourPicker). Come un cambio centro dallo step "dove" si torna a "qualsiasi operatore":
// l'operatore scelto nell'altro centro può non lavorare nell'origin. Se un operatore era
// scelto, la scheda dell'origin in «Dove vuoi andare» lo ricorda.
// Restituisce true quando si torna all'origin.
export const applyMultiStorePointOfSales =
  (ids: string[]): AppThunk<boolean> =>
  (dispatch, getState) => {
    const { multiStore, pointOfSale, appointmentSlot, user } = getState();
    const origin = multiStore.originPointOfSale;
    if (origin === undefined) return false;

    const { addedIds, isReturningToOrigin } = resolveDraftApply(
      ids,
      origin.id,
      pointOfSale.activePoint.id,
      MULTI_STORE_MAX_ADDED
    );
    if (isReturningToOrigin) {
      const { date, hour } = appointmentSlot;
      if (date !== undefined && hour !== undefined) {
        dispatch(switchMultiStorePointOfSale(origin, true, date, hour));
      } else {
        dispatch(setPointOfSaleFromCustomPointOfSaleId(origin));
        dispatch(resetUser());
        dispatch(
          setMultiStoreAnyUserPointOfSale(
            getAnyUserPointOfSaleId(
              origin.id,
              true,
              user.activeUser !== undefined
            )
          )
        );
      }
    }
    dispatch(setMultiStorePointOfSales(addedIds));
    return isReturningToOrigin;
  };

const EMPTY_IDS: string[] = [];

const selectMultiStore = (state: RootState): MultiStoreSliceInterface =>
  state.multiStore;
const selectServiceId = (state: RootState): string =>
  state.typeOfAppointment.value.id;
const selectCurrentPointOfSale = (state: RootState): PointOfSale =>
  state.pointOfSale.activePoint;

// Centri inclusi (origin + aggiunti) della sessione valida, altrimenti nessuno
export const selectedIncludedPointOfSaleIds = createSelector(
  [selectMultiStore, selectServiceId, selectCurrentPointOfSale],
  (multiStore, serviceId, current): string[] =>
    multiStore.originPointOfSale !== undefined &&
    isSessionValid(multiStore, serviceId, current.id)
      ? [multiStore.originPointOfSale.id, ...multiStore.addedPointOfSaleIds]
      : EMPTY_IDS
);

// Centro da cui si cercano le alternative: l'origin della sessione valida, altrimenti il corrente
export const selectedMultiStoreOrigin = (state: RootState): PointOfSale =>
  state.multiStore.originPointOfSale !== undefined &&
  isSessionValid(
    state.multiStore,
    state.typeOfAppointment.value.id,
    state.pointOfSale.activePoint.id
  )
    ? state.multiStore.originPointOfSale
    : state.pointOfSale.activePoint;

// Centro corrente raggiunto azzerando l'operatore scelto, finché il cliente non ne sceglie
// un altro: la sua scheda dice «Con il primo operatore disponibile»
export const selectedMultiStoreAnyUserPointOfSaleId = (
  state: RootState
): string | undefined =>
  getActiveAnyUserPointOfSaleId(
    state.multiStore.anyUserPointOfSaleId,
    state.pointOfSale.activePoint.id,
    state.user.activeUser !== undefined
  );

export const selectedIsMultiStorePanelOpen = (state: RootState): boolean =>
  state.multiStore.isPanelOpen &&
  isSessionValid(
    state.multiStore,
    state.typeOfAppointment.value.id,
    state.pointOfSale.activePoint.id
  );

// eslint-disable-next-line import/no-default-export
export default MultiStoreSlice.reducer;
